import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase, initError } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track whether the first auth event has been received
  const initializedRef = useRef(false);
  const fallbackTimerRef = useRef(null);

  useEffect(() => {
    if (!supabase) {
      console.error('Supabase not available:', initError);
      setError(initError || 'Database unavailable');
      setLoading(false);
      return;
    }

    let mounted = true;

    const finishLoading = () => {
      if (fallbackTimerRef?.current) {
        clearTimeout(fallbackTimerRef?.current);
        fallbackTimerRef.current = null;
      }
      if (mounted) {
        setLoading(false);
      }
    };

    // Fallback: if onAuthStateChange never fires within 8s, stop loading gracefully
    fallbackTimerRef.current = setTimeout(() => {
      if (!initializedRef?.current && mounted) {
        console.warn('Auth: no session event received within 8s, continuing as unauthenticated');
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    }, 8000);

    // Single source of truth: onAuthStateChange handles both initial session
    // and all subsequent auth state changes. Supabase fires INITIAL_SESSION
    // (or SIGNED_IN) immediately on mount with the persisted session from
    // localStorage — no network round-trip needed, no timeout risk.
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        // Mark that we received at least one auth event
        initializedRef.current = true;

        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          await loadUserProfile(currentUser?.id, finishLoading);
        } else {
          setUserProfile(null);
          finishLoading();
        }
      }
    );

    return () => {
      mounted = false;
      if (fallbackTimerRef?.current) {
        clearTimeout(fallbackTimerRef?.current);
      }
      subscription?.unsubscribe();
    };
  }, []);

  const loadUserProfile = async (userId, onComplete) => {
    if (!supabase) {
      onComplete?.();
      return;
    }

    try {
      const { data, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (profileError) {
        console.error('Profile load error:', profileError);
      } else {
        setUserProfile(data);
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      onComplete?.();
    }
  };

  const signIn = async (email, password) => {
    if (!supabase) throw new Error('Database unavailable');

    const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (!supabase) throw new Error('Database unavailable');

    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;

    setUser(null);
    setUserProfile(null);
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    signIn,
    signOut,
  };

  if (error && !user && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Configuration Error</h2>
          <p className="text-gray-700 mb-4 text-center">{error}</p>
          <p className="text-sm text-gray-600 text-center mb-6">
            Please ensure your environment variables are properly configured.
          </p>
          <div className="bg-gray-100 rounded p-4 text-xs font-mono text-gray-800">
            <p>Required variables:</p>
            <p className="mt-2">• VITE_SUPABASE_URL</p>
            <p>• VITE_SUPABASE_ANON_KEY</p>
          </div>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
